import React from 'react';
import { cloneDeep, get } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { StoreApplicant } from '@funnels/types';
import { getApplicantName, run } from '@funnels/utils';

import PrettyTextList from '../../../../PrettyTextList';
import { OtherApplicantLink } from './index.style';

export interface CopyFromOtherApplicantsProps {
  list: StoreApplicant[];
  applicantIndex: number;
  addressIndex: number;
}

function CopyFromOtherApplicants(props: CopyFromOtherApplicantsProps) {
  const { tracking } = useAnalytics();
  const [, setFormValue] = useFormValues();

  const borrowerApplicantsWithAddress = props.list
    // Step 1: Filter out Guarantors
    .filter((a: StoreApplicant, index: number) => {
      return get({ applicants: props.list }, FIELD_PATHS.APPLICANT_TYPE(index)) !== FIELD_VALUES.GUARANTOR;
    })
    // Step 2: Filter by address present
    .filter((a: StoreApplicant, index: number) => {
      if (props.addressIndex === 0) {
        return get({ applicants: props.list }, FIELD_PATHS.APPLICANT_CURRENT_ADDRESS_RESIDENTIAL(index), false);
      } else {
        return get(
          { applicants: props.list },
          FIELD_PATHS.APPLICANT_PREVIOUS_ADDRESS_RESIDENTIAL(index, props.addressIndex - 1),
          false
        );
      }
    });
  const borrowerFullNames = borrowerApplicantsWithAddress.map((a: StoreApplicant, index: number) =>
    getApplicantName(props.list, a.id, `Other applicant ${index + 1}`)
  );

  function handleCopy(addressIndex: number, index: number) {
    const otherApplicant = props.list[index];

    if (!otherApplicant) {
      return;
    }

    if (addressIndex === 0) {
      const address = cloneDeep(get({ applicants: [otherApplicant] }, FIELD_PATHS.APPLICANT_CURRENT_ADDRESS(0)));
      const currentMailingAddress = cloneDeep(
        get({ applicants: [otherApplicant] }, FIELD_PATHS.APPLICANT_MAILING_ADDRESS(0))
      );
      const currentMailingAddressFormatted = cloneDeep(
        get({ applicants: [otherApplicant] }, FIELD_PATHS.APPLICANT_MAILING_ADDRESS_FORMATTED(0))
      );

      if (address) {
        setFormValue(FIELD_PATHS.APPLICANT_CURRENT_ADDRESS(props.applicantIndex), address);
      }

      if (currentMailingAddress && currentMailingAddressFormatted) {
        setFormValue(FIELD_PATHS.APPLICANT_MAILING_ADDRESS(props.applicantIndex), currentMailingAddress);
        setFormValue(
          FIELD_PATHS.APPLICANT_MAILING_ADDRESS_FORMATTED(props.applicantIndex),
          currentMailingAddressFormatted
        );
      }
    } else {
      const address = get(
        { applicants: [otherApplicant] },
        FIELD_PATHS.APPLICANT_PREVIOUS_ADDRESS(0, addressIndex - 1)
      );

      if (address) {
        setFormValue(FIELD_PATHS.APPLICANT_PREVIOUS_ADDRESS(props.applicantIndex, addressIndex - 1), address);
      }
    }
  }

  if (borrowerFullNames.length === 0) {
    return null;
  }

  return (
    <div>
      Copy details from{' '}
      <PrettyTextList
        items={borrowerFullNames}
        lastSeparator=" or "
        format={(fullName: string, index: number) => {
          const trackingEvent = `Copy from ${fullName}`;

          return (
            <OtherApplicantLink
              onClick={run(
                () => handleCopy(props.addressIndex, index),
                () => tracking?.button.link({ text: trackingEvent })
              )}
            >
              {fullName}
            </OtherApplicantLink>
          );
        }}
      />
      .
    </div>
  );
}

export default CopyFromOtherApplicants;
